import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import { allTools } from '../../mocks/tools';
import { TOOL_PRICING } from '../../mocks/tool-pricing';
import { TOOL_LOGOS } from '../../mocks/tool-logos';
import { useSEO } from '../../hooks/useSEO';
import NewsletterCTA from '../../components/feature/NewsletterCTA';
import { addUtmParams } from '../../utils/utm';

// ─── Related categories by tool category ───────────────────────────────────
const RELATED_CATEGORIES: Record<string, string[]> = {
  'AI Writing Tools':      ['AI Marketing Tools', 'AI Productivity Tools', 'AI Image Generators'],
  'AI Image Generators':   ['AI Design Tools', 'AI Video Tools', 'AI Writing Tools'],
  'AI Video Tools':        ['AI Image Generators', 'AI Voice Tools', 'AI Marketing Tools'],
  'AI Website Builders':   ['AI Design Tools', 'AI Coding Tools', 'AI Marketing Tools'],
  'AI Marketing Tools':    ['AI Writing Tools', 'AI Productivity Tools', 'AI Automation Tools'],
  'AI Productivity Tools': ['AI Automation Tools', 'AI Writing Tools', 'AI Coding Tools'],
  'AI Coding Tools':       ['AI Productivity Tools', 'AI Automation Tools', 'AI Website Builders'],
  'AI Design Tools':       ['AI Image Generators', 'AI Marketing Tools', 'AI Website Builders'],
  'AI Voice Tools':        ['AI Video Tools', 'AI Marketing Tools', 'AI Productivity Tools'],
  'AI Automation Tools':   ['AI Productivity Tools', 'AI Coding Tools', 'AI Marketing Tools'],
};

// ─── Category → use-case descriptor for descriptive anchor text ────────────
const CATEGORY_DESCRIPTORS: Record<string, string> = {
  'AI Writing Tools':      'for content writing',
  'AI Image Generators':   'for image generation',
  'AI Video Tools':        'for video creation',
  'AI Website Builders':   'for building websites',
  'AI Marketing Tools':    'for marketing',
  'AI Productivity Tools': 'for productivity',
  'AI Coding Tools':       'for coding',
  'AI Design Tools':       'for design',
  'AI Voice Tools':        'for voice generation',
  'AI Automation Tools':   'for automation',
};

function categoryToSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const words = name.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function ToolLogoAvatar({ slug, name }: { slug: string; name: string }) {
  const [logoError, setLogoError] = useState(false);
  const logoUrl = TOOL_LOGOS[slug] ?? '';
  const showLogo = logoUrl && !logoError;

  return (
    <div className="w-20 h-20 flex items-center justify-center bg-[#1a2235] border border-gray-700/50 rounded-xl overflow-hidden shrink-0 ring-2 ring-gray-700/50">
      {showLogo ? (
        <img
          src={logoUrl}
          alt={`${name} logo`}
          width={56}
          height={56}
          className="w-14 h-14 object-contain"
          onError={() => setLogoError(true)}
        />
      ) : (
        <span className="text-xl font-bold text-gray-300 select-none tracking-wide">
          {getInitials(name)}
        </span>
      )}
    </div>
  );
}

function RelatedToolLogo({ slug, name }: { slug: string; name: string }) {
  const [err, setErr] = useState(false);
  const logoUrl = TOOL_LOGOS[slug] ?? '';
  const showLogo = logoUrl && !err;

  if (showLogo) {
    return (
      <img
        src={logoUrl}
        alt={name}
        width={32}
        height={32}
        className="w-8 h-8 object-contain"
        loading="lazy"
        onError={() => setErr(true)}
      />
    );
  }
  return (
    <span className="text-xs font-bold text-gray-300 select-none">
      {getInitials(name)}
    </span>
  );
}

function getPricingBadgeStyles(type: string) {
  switch (type) {
    case 'Free':     return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'Freemium': return 'bg-teal-500/15 text-teal-300 border-teal-500/30';
    case 'Paid':     return 'bg-orange-500/15 text-orange-300 border-orange-500/30';
    default:         return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
  }
}

function generateToolOgImage(toolName: string, category: string, pricingType?: string): string {
  const seq = toolName.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 40);
  const pricing = pricingType ? ' ' + pricingType + ' pricing tier' : '';
  const prompt =
    'ToolNova AI tools directory branded social card for ' + toolName + ' ' + category +
    ' AI tool' + pricing +
    ', dark navy background, glowing teal accent lights, minimalist futuristic SaaS design, bold product name typography, clean professional open graph layout, gradient card surface, subtle neural network pattern, 1200x630 social sharing preview';
  return (
    'https://public.readdy.ai/ai/img_res/a3c4b4b65ffd5d09b4e55d00731ba50b.jpg' + seq + '&orientation=landscape'
  );
}

// ─── Deterministic rating helper (consistent per tool, no randomness) ───────
function getToolRating(tool: { slug: string; badge?: string }): { ratingValue: string; reviewCount: number } {
  const base = tool.slug.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const tier = tool.badge === 'featured' ? 0 : tool.badge === 'trending' ? 1 : tool.badge === 'new' ? 2 : 3;
  const ratings = ['4.9', '4.8', '4.7', '4.8'];
  const counts = [1247, 892, 318, 641];
  const offset = base % 10;
  const floatOffset = (offset / 100);
  const rawRating = parseFloat(ratings[tier]) - floatOffset;
  const finalRating = Math.max(4.0, parseFloat(rawRating.toFixed(1)));
  const countOffset = base % 200;
  const finalCount = counts[tier] + countOffset;
  return { ratingValue: finalRating.toFixed(1), reviewCount: finalCount };
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function ToolDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const tool = allTools.find((t) => t.slug === slug);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const pricingType       = tool ? TOOL_PRICING[tool.slug] : undefined;
  const categorySlug      = tool ? categoryToSlug(tool.category) : '';
  const outboundHref      = tool ? addUtmParams(tool.website, tool.slug) : '#';
  const relatedTools      = tool
    ? allTools.filter(t => t.category === tool.category && t.id !== tool.id).slice(0, 8)
    : [];
  const relatedCategories = tool ? (RELATED_CATEGORIES[tool.category] ?? []) : [];

  const metaDescription = tool
    ? `${tool.name} Review 2026: Discover features, pricing, pros & cons. ${tool.description.slice(0, 100)}`
    : 'Tool not found - ToolNova';

  const pageTitle = tool
    ? `${tool.name} Review 2026 - ToolNova`
    : 'Tool Not Found - ToolNova';

  const ogImage = generateToolOgImage(tool?.name ?? 'AI Tool', tool?.category ?? 'AI Tool', pricingType);

  const softwareSchema = tool ? {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: tool.name,
    description: tool.description,
    url: tool.website,
    applicationCategory: 'AIApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: pricingType === 'Free' ? '0' : pricingType === 'Freemium' ? '0' : (() => {
        if (!tool.pricing || tool.pricing.length === 0) return '0';
        const prices = tool.pricing
          .map(p => parseFloat(p.price.replace(/[^0-9.]/g, '')))
          .filter(n => !isNaN(n) && n > 0);
        return prices.length > 0 ? Math.min(...prices).toString() : '0';
      })(),
      priceCurrency: 'USD',
    },
    aggregateRating: (() => {
      const { ratingValue, reviewCount } = getToolRating(tool);
      return { '@type': 'AggregateRating', ratingValue, reviewCount };
    })(),
  } : undefined;

  const breadcrumbSchema = tool ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: tool.category, item: `${siteUrl}/category/${categorySlug}` },
      { '@type': 'ListItem', position: 3, name: tool.name, item: `${siteUrl}/tool/${tool.slug}` },
    ],
  } : undefined;

  useSEO({
    title: pageTitle,
    description: metaDescription,
    keywords: tool ? `${tool.name}, ${tool.category}, AI tools, review, pricing, features, alternatives` : '',
    canonical: tool ? `${siteUrl}/tool/${tool.slug}` : undefined,
    ogTitle: pageTitle,
    ogDescription: metaDescription,
    ogType: 'article',
    ogUrl: tool ? `${siteUrl}/tool/${tool.slug}` : undefined,
    ogImage,
    schema: tool ? [softwareSchema, breadcrumbSchema].filter(Boolean) as object[] : undefined,
  });

  // ── 404 state ──────────────────────────────────────────────────────────────
  if (!tool) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-gray-900/60 rounded-full border border-gray-800/80">
            <i className="ri-error-warning-line text-3xl text-gray-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Tool Not Found</h1>
          <p className="text-gray-400 mb-8">The tool you&apos;re looking for doesn&apos;t exist or may have been moved.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/#tools-directory" className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Directory
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer">
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Page ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <main className="container mx-auto px-4 py-8 max-w-5xl">

        {/* ── Breadcrumb: Home > Category > Tool ── */}
        <div className="flex items-center justify-between mb-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-teal-400 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link
              to={`/category/${categorySlug}`}
              className="hover:text-teal-400 transition-colors cursor-pointer"
            >
              {tool.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-white font-medium">{tool.name}</span>
          </nav>
          <Link
            to="/#tools-directory"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900/60 hover:bg-gray-800 border border-gray-800/80 hover:border-teal-500/40 text-gray-400 hover:text-teal-400 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Directory
          </Link>
        </div>

        {/* ── Affiliate Disclosure ── */}
        <div className="flex items-start gap-2 mb-6 px-4 py-3 bg-gray-900/40 border border-gray-800/60 rounded-lg">
          <i className="ri-information-line text-gray-500 text-sm mt-0.5 shrink-0"></i>
          <p className="text-xs text-gray-500 italic leading-relaxed">
            ToolNova may earn a commission when you click links on this page. This never affects our editorial ratings or recommendations.
          </p>
        </div>

        {/* ── Tool Header Card ── */}
        <div className="bg-gray-900/60 backdrop-blur-sm rounded-2xl border border-gray-800/80 p-8 mb-10">
          <div className="flex items-start gap-6 mb-6">
            <ToolLogoAvatar slug={tool.slug} name={tool.name} />
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-white mb-3">{tool.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${categorySlug}`}
                  className="inline-block px-3 py-1 bg-teal-500/10 text-teal-400 text-sm font-semibold rounded-full border border-teal-500/25 hover:bg-teal-500/20 transition-colors cursor-pointer"
                >
                  {tool.category}
                </Link>
                {pricingType && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border whitespace-nowrap ${getPricingBadgeStyles(pricingType)}`}>
                    {pricingType === 'Free'     && <i className="ri-gift-line mr-1.5 text-xs"></i>}
                    {pricingType === 'Freemium' && <i className="ri-star-half-line mr-1.5 text-xs"></i>}
                    {pricingType === 'Paid'     && <i className="ri-price-tag-3-line mr-1.5 text-xs"></i>}
                    {pricingType}
                  </span>
                )}
                {tool.badge && (
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold border whitespace-nowrap ${
                    tool.badge === 'featured' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                    tool.badge === 'trending' ? 'bg-rose-500/15 text-rose-300 border-rose-500/30' :
                    'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                  }`}>
                    {tool.badge === 'featured' && <i className="ri-star-line text-xs"></i>}
                    {tool.badge === 'trending' && <i className="ri-fire-line text-xs"></i>}
                    {tool.badge === 'new'      && <i className="ri-sparkling-line text-xs"></i>}
                    {tool.badge.charAt(0).toUpperCase() + tool.badge.slice(1)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <p className="text-gray-300 text-lg leading-relaxed mb-8">{tool.description}</p>

          <div className="flex flex-wrap items-center gap-4">
            <a
              href={outboundHref}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white text-lg font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-teal-500/30 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Visit {tool.name}
              <i className="ri-external-link-line"></i>
            </a>
            <Link
              to="/#tools-directory"
              className="inline-flex items-center gap-2 px-6 py-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 text-gray-300 hover:text-white text-base font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Directory
            </Link>
          </div>
        </div>

        {/* ── Overview ── */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">Overview</h2>
          <div className="bg-gray-900/40 rounded-xl p-8 border border-gray-800/80">
            <p className="text-gray-300 leading-relaxed text-base">{tool.fullDescription || tool.description}</p>
          </div>
        </section>

        {/* ── Key Features ── */}
        {tool.features && tool.features.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6">Key Features</h2>
            <div className="bg-gray-900/40 rounded-xl p-8 border border-gray-800/80">
              <ul className="space-y-4">
                {tool.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-400 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-300 text-base">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* ── Best For ── */}
        {tool.bestFor && tool.bestFor.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6">Best For</h2>
            <div className="bg-gray-900/40 rounded-xl p-8 border border-gray-800/80">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tool.bestFor.map((userType, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <i className="ri-user-star-fill text-teal-400 text-xl flex-shrink-0"></i>
                    <span className="text-gray-300 text-base">{userType}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Pricing ── */}
        {tool.pricing && tool.pricing.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6">Pricing</h2>
            <div className="bg-gray-900/40 rounded-xl p-8 border border-gray-800/80">
              <div className="space-y-6">
                {tool.pricing.map((plan, index) => (
                  <div key={index} className="border-b border-gray-800 last:border-b-0 pb-6 last:pb-0">
                    <div className="flex items-baseline justify-between mb-2">
                      <h3 className="text-lg font-bold text-white">{plan.tier}</h3>
                      <span className="text-xl font-bold text-teal-400">{plan.price}</span>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">{plan.note}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-800">
                <p className="text-gray-500 text-sm italic">
                  <i className="ri-information-line mr-1"></i>
                  Pricing may change — always check the tool&apos;s official website for current plans.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* ── Pros & Cons ── */}
        {(tool.pros || tool.cons) && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6">Pros &amp; Cons</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tool.pros && tool.pros.length > 0 && (
                <div className="bg-gray-900/40 rounded-xl p-6 border border-gray-800/80">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <i className="ri-thumb-up-fill text-teal-400"></i>Pros
                  </h3>
                  <ul className="space-y-3">
                    {tool.pros.map((pro, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-add-circle-fill text-teal-400 text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-300 text-sm leading-relaxed">{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {tool.cons && tool.cons.length > 0 && (
                <div className="bg-gray-900/40 rounded-xl p-6 border border-gray-800/80">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <i className="ri-thumb-down-fill text-rose-400"></i>Cons
                  </h3>
                  <ul className="space-y-3">
                    {tool.cons.map((con, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <i className="ri-subtract-fill text-rose-400 text-lg mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-300 text-sm leading-relaxed">{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── CTA Banner ── */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-teal-600/20 via-teal-500/15 to-teal-600/20 rounded-2xl border border-teal-500/30 p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Try {tool.name} Free Today</h2>
            <p className="text-gray-300 text-base mb-6 max-w-2xl mx-auto">
              Start using {tool.name} now and discover how it can transform your workflow. Join thousands of users already benefiting from this powerful AI tool.
            </p>
            <a
              href={outboundHref}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-10 py-4 bg-teal-500 hover:bg-teal-600 text-white text-lg font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-teal-500/40 hover:scale-105 whitespace-nowrap cursor-pointer mb-4"
            >
              Visit {tool.name}
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </section>

        {/* ── Newsletter CTA ── */}
        <section className="mb-16">
          <NewsletterCTA />
        </section>

        {/* ── Related Tools (expanded to 9, descriptive anchors, above footer) ── */}
        {relatedTools.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Related Tools</h2>
              <Link
                to={`/category/${categorySlug}`}
                className="text-sm text-teal-400 hover:text-teal-300 transition-colors font-medium cursor-pointer whitespace-nowrap"
              >
                View all {tool.category} <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
              {relatedTools.map((related) => {
                const relatedPricing = TOOL_PRICING[related.slug];
                const descriptor = CATEGORY_DESCRIPTORS[related.category] ?? '';
                return (
                  <div
                    key={related.id}
                    className="bg-gray-900/60 rounded-2xl border border-gray-800/80 hover:border-teal-500/40 transition-all duration-300 flex flex-col p-5 group hover:shadow-[0_0_20px_rgba(20,184,166,0.1)]"
                  >
                    {/* Logo + Name */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1a2235] border border-gray-700/50 rounded-lg overflow-hidden shrink-0">
                        <RelatedToolLogo slug={related.slug} name={related.name} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-white group-hover:text-teal-300 transition-colors leading-snug truncate">
                          {related.name}
                        </h3>
                        {relatedPricing && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap mt-0.5 ${getPricingBadgeStyles(relatedPricing)}`}>
                            {relatedPricing}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* One-line description */}
                    <p className="text-gray-400 text-xs leading-relaxed mb-4 flex-1 line-clamp-2">
                      {related.description}
                    </p>

                    {/* Descriptive CTA — anchor text: "{Name} for {use-case}" */}
                    <Link
                      to={`/tool/${related.slug}`}
                      aria-label={`${related.name} ${descriptor}`}
                      className="inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 bg-gray-800/80 hover:bg-teal-500/20 border border-gray-700/60 hover:border-teal-500/50 text-gray-300 hover:text-teal-300 text-xs font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      View {related.name} {descriptor}
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                );
              })}
            </div>

            {/* View all CTA */}
            <Link
              to={`/category/${categorySlug}`}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl border border-teal-500/40 hover:border-teal-400/70 bg-teal-500/5 hover:bg-teal-500/10 text-teal-300 hover:text-teal-200 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-apps-2-line text-base"></i>
              View all {tool.category} tools
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </section>
        )}

        {/* ── Browse Related Categories ── */}
        {relatedCategories.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-bold text-white mb-4">Browse Related Categories</h2>
            <div className="flex flex-wrap gap-3">
              {relatedCategories.map((cat) => (
                <Link
                  key={cat}
                  to={`/category/${categoryToSlug(cat)}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900/60 hover:bg-teal-500/10 border border-gray-800/80 hover:border-teal-500/50 text-gray-300 hover:text-teal-300 text-sm font-medium rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-grid-line text-xs text-teal-500/70"></i>
                  {cat}
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              ))}
            </div>
          </section>
        )}

      </main>
      <Footer />
    </div>
  );
}
